import {Component, computed, inject} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {NgClass, NgIf} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormMode} from '../../../shared/types/common';
import {DrawerFormBase} from '../drawer-form-base';
import {ManagementStore} from '../management.store';
import {CommunicationService} from '../../../http/communication.service';
import {StateService} from '../../../services/state.service';
import {AuthorizationService} from '../../../services/authorization.service';
import {Permissions} from '../../../shared/permissions';
import {CustomValidators} from '../../../shared/validators';
import {objectArrayToSelectOptions, stringArrayToSelectOptions} from '../../../utils/mappings';
import {AccountManagementColumn, User, UserPayload} from '../types';
import {TitledInputComponent} from '../../../shared/components/titled-input/titled-input.component';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {SelectMenuComponent, SelectMode} from '../../../shared/components/select-menu/select-menu.component';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';

@Component({
  selector: 'app-user-drawer',
  imports: [ReactiveFormsModule, NgClass, NgIf, TitledInputComponent, ButtonComponent, SelectMenuComponent, LoaderComponent],
  templateUrl: './user-drawer.component.html',
  styleUrl: './user-drawer.component.scss',
})
export class UserDrawerComponent extends DrawerFormBase<User> {
  public form: FormGroup<{
    email: FormControl<string | null>;
    firstName: FormControl<string | null>;
    lastName: FormControl<string | null>;
    role: FormControl<string | null>;
    accountKey: FormControl<string | null>;
  }>;
  public readonly configs = {
    [FormMode.ADD]: {title: 'Add user', submitText: 'Add', submit: () => this.addUser()},
    [FormMode.EDIT]: {title: 'Edit user', submitText: 'Update', submit: () => this.editUser()},
  };

  public isVerified = false;
  public canDelete = false;
  protected readonly SelectMode = SelectMode;

  // Surface the user's current role so the field reflects reality instead of the "Select role"
  // placeholder. When the role isn't one of the assignable constants (e.g. the system-assigned
  // "Trial"), add it as a display-only (disabled) option: it shows as the current value but stays
  // non-assignable, preserving the prior behaviour where only Prospect/Client/Business/Admin are pickable.
  protected readonly rolesOptions = computed(() => {
    const options = stringArrayToSelectOptions(this.store.roles());
    const current = this.entity?.role;
    if (current && !options.some(option => option.key === current)) {
      return [...options, {key: current, label: current, disabled: true}];
    }
    return options;
  });
  protected readonly accountOptions = computed(() =>
    objectArrayToSelectOptions(this.store.accountSummaries(), AccountManagementColumn.ACCOUNT_KEY, AccountManagementColumn.NAME));

  private readonly fb = inject(FormBuilder);
  private readonly store = inject(ManagementStore);
  private readonly communicationService = inject(CommunicationService);
  private readonly stateService = inject(StateService);
  private readonly authorizationService = inject(AuthorizationService);

  constructor() {
    super();
    this.isVerified = !!this.entity?.verifiedEmail;
    this.canDelete = this.authorizationService.hasPermissions([Permissions.DELETE_USER]);
    this.store.ensureRoles();
    this.store.ensureAccountSummaries();

    this.form = this.fb.group({
      email: new FormControl({value: '', disabled: !!this.entity}, [CustomValidators.required('Email'), CustomValidators.email('Email'), CustomValidators.businessEmail('Email')]),
      firstName: new FormControl('', [CustomValidators.required('First Name')]),
      lastName: new FormControl('', [CustomValidators.required('Last Name')]),
      role: new FormControl('', [CustomValidators.required('Role')]),
      accountKey: new FormControl<string>(''),
    });

    if (this.entity) {
      this.currentMode = FormMode.EDIT;
      this.form.patchValue({
        ...this.entity,
        accountKey: this.entity.account ? this.entity.account.key : undefined,
      });
    }
  }

  get email() {
    return this.form.get('email') as FormControl;
  }

  get firstName() {
    return this.form.get('firstName') as FormControl;
  }

  get lastName() {
    return this.form.get('lastName') as FormControl;
  }

  get role() {
    return this.form.get('role') as FormControl;
  }

  get accountKey() {
    return this.form.get('accountKey') as FormControl;
  }

  submit(): void {
    if (this.saving() || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.configs[this.currentMode].submit();
  }

  private addUser(): void {
    this.store.addUser(this.getUserPayload())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.stateService.updateAccountsOnUserUpdate(result.account, result.email);
          this.onSuccess(`User "${result.firstName}" added successfully`);
        },
        error: error => this.onError(error),
      });
  }

  private editUser(): void {
    this.store.editUser(this.getUserPayload())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          if (this.authorizationService.userProfile.email === result.email) {
            this.authorizationService.userProfile = {
              email: result.email,
              firstName: result.firstName,
              lastName: result.lastName,
            };
          }
          this.stateService.updateAccountsOnUserUpdate(result.account, result.email);
          this.onSuccess(`User "${result.firstName}" updated successfully`);
        },
        error: error => this.onError(error),
      });
  }

  resendInvite(): void {
    this.communicationService.httpPost('/users/send-password-reset-email', {email: this.entity?.email})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.utilService.openSnackBar('Invite sent successfully', {variant: 'success', position: 'center', duration: 3000}),
        error: () => this.utilService.openSnackBar('Failed to send Invite', {variant: 'error', position: 'center', duration: 3000}),
      });
  }

  deleteUser(): void {
    if (!this.entity) {
      return;
    }
    this.confirmAndDelete(
      {
        header: 'Delete user',
        description: `This permanently deletes ${this.entity.firstName} ${this.entity.lastName} (${this.entity.email}) and all of their data — chats, alerts and trials. This action is irreversible.`,
        confirmLabel: 'Delete user',
        expectedValue: this.entity.email,
      },
      () => this.store.deleteUser(this.entity!.id),
      `User "${this.entity.firstName}" deleted successfully`,
    );
  }

  private getUserPayload(): UserPayload {
    return {...this.form.getRawValue(), id: this.entity?.id} as UserPayload;
  }
}
